import React, { useEffect, useState } from 'react'

import {
  Tabs,
  Tab,
  CardContent,
  Card,
  CardActions,
  useTheme,
  makeStyles,
  useMediaQuery
} from '@material-ui/core'

import {
  fetchBalanceData,
  calculateBalance,
  calculateBalanceRows,
  recalculateBalance,
  fetchBalances,
  saveBalance,
  resetOperationsModifiedFlag,
  getStatusBalance,
  enhanceBalanceDataWithHighlighting,
  enhanceBalanceDataWithColumnHighlighting
} from './balances.services'

import { BalanceModel, StatusBalanceResponse } from './balances.model'
import moment from 'moment'
import {
  GlobalerisDataGrid,
  DataGridOptions,
  DataGridUpdatedRow,
  useNotify,
  DataGridColumn,
  UserPermission,
  XLSXExport,
  OnEventReceived,
  CellIconConfiguration
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'
import DateFilter, { DateFilterPeriods } from '../../components/shared/dateFilter/dateFilter'
import { DateFilters, ErrorCodes } from '../../model/enums'
import { DatePeriod, GlobalNotification } from '../../model'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { useReferredState } from '../../hooks/useReferredState'
import { useSelector } from 'react-redux'
import { RootStoreModel } from '../../redux'
import { useHttpError } from '../../hooks/useHttpError'
import { styles } from './balance.styles'
import { getUserPermissions, hasPermission } from '../../helpers/permissionsHelper'
import { Pages, Resources } from '../../model/RBAC/constants'
import ButtonWithPermissions from '../../components/buttonWithPermissions/buttonWithPermissions'
import { BalanceResponsivePage } from './responsive/balance'
import { NotificationMessageIcons } from '../../components'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import { EnumTipoBalance, EnumZonaBalance } from '../../api'
import { enumKeyToValue } from '../../helpers/enumHelper'
import { formatKwh } from './balances.mapper'

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

export const BalancePage: React.FC = () => {
  const [t] = useTranslation(['globalerisLibrary', 'translation', 'balance'])
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, getEventsReceived, addEventListener, deleteEventListener } = useNotify()

  const [balanceStatus, setBalanceStatus] = useState<StatusBalanceResponse>({ inProcess: false })

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const matchesSmallDevices = useMediaQuery(currentTheme.breakpoints.down('sm'))

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )
  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [balances, setBalances] = React.useState<BalanceModel[] | null>(null)
  const [activeBalance, activeBalanceRef, setActiveBalance] = useReferredState<BalanceModel | null>(
    null
  )
  const [activeBalanceColumns, setActiveBalancecolumns] = React.useState<
    DataGridColumn[] | undefined
  >([])
  const [activeTab, setActiveTab] = React.useState(0)
  const [balancePeriod, setBalancePeriod] = React.useState<DatePeriod>({
    start: matchesSmallDevices ? moment().toDate() : moment().add(-5, 'days').toDate(),
    end: matchesSmallDevices ? moment().toDate() : moment().add(15, 'days').toDate()
  })
  const [pendingChanges, setPendingChanges] = React.useState<boolean>(false)
  const [todayIndexes, setTodayIndexes] = React.useState<number[]>([])
  const [pastDatesIndexes, setPastDatesIndexes] = React.useState<number[]>([])
  const [isFetching, setIsFetching] = React.useState<boolean>(true)

  const [nextRequestedTab, nextRequestedTabRef, setNextRequestedTab] = useReferredState<
    number | null
  >(null)

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const cellIconConfig: CellIconConfiguration = {
    propertyName: 'hasInfo',
    getTooltipText: (row, columnName) => {
      // Check if THIS specific cell is highlighted
      const isCellHighlighted = row[`${columnName}Highlighted`]
      const hasColumnInfo = row[`${columnName}hasInfo`]

      // Only show icon on highlighted cells with info
      if (!isCellHighlighted || !hasColumnInfo) {
        return undefined as any // Return undefined to hide icon for this cell
      }

      const tsoQuantity = row[`${columnName}tsoQuantity`]
      if (tsoQuantity !== undefined) {
        return t('balance:dataGrid.cellIcon.tsoQuantity', {
          quantity: formatKwh(tsoQuantity)
        })
      }
      return t('balance:dataGrid.cellIcon.defaultTooltip')
    },
    onClick: (row, columnName, rowIndex) => {
      const tsoQuantity = row[`${columnName}tsoQuantity`]
      const cellValue = row[columnName]
      if (tsoQuantity !== undefined && cellValue !== undefined) {
        const cleanValue =
          typeof cellValue === 'string' ? parseFloat(cellValue.replace('*', '')) : cellValue
        const difference = cleanValue - tsoQuantity
        const sign = difference > 0 ? '+' : ''
        const columnLabel =
          activeBalanceColumns?.find(col => col.name === columnName)?.label || columnName
        const message = (
          <>
            {row.date} - {columnLabel}:
            <br />
            TSO: {formatKwh(tsoQuantity)}
            <br />
            {t('balance:dataGrid.cellIcon.difference')}: {sign}
            {formatKwh(difference)}
          </>
        )
        info(message)
      }
    }
  }

  const options: DataGridOptions = {
    tableHeight: 600,
    columnWidth: 110,
    elevation: 0,
    roundDecimals: 0,
    highlightedCellTextColor:
      (currentTheme as any).dataGrid?.highlightedCell?.textColor || '#e10019',
    cellIconConfig: cellIconConfig
  }

  //#region Handlers

  const onClickTabHandler = async (balance: BalanceModel, tab: number) => {
    if (activeTab !== tab) {
      if (pendingChanges) {
        await getConfirmation(ConfirmationDialogs.DISCARD_CHANGES).then(async () => {
          if (nextRequestedTabRef.current !== null) {
            setActiveTab(nextRequestedTabRef.current)
          }
          resetBalanceData(balance)
          setPendingChanges(false)
          closeConfirmation()

          getBalanceData(balance)
        })
      } else {
        clearError()
        getBalanceData(balance)
      }
    }
  }

  const onChangeTabHandler = (event: React.ChangeEvent<{}>, tab: number) => {
    if (activeTab !== tab) {
      if (pendingChanges) {
        setNextRequestedTab(tab)
      } else {
        setActiveTab(tab)
      }
    }
  }

  const onSelectDateHandler = React.useCallback((startDate: Date, endDate: Date): void => {
    setBalancePeriod({ ...balancePeriod, start: startDate, end: endDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onValidatePrerequisites = async (): Promise<boolean> => {
    if (pendingChanges) {
      let discard: boolean = false
      discard = await getConfirmation(ConfirmationDialogs.DISCARD_CHANGES).then(async () => {
        closeConfirmation()
        return true
      })

      return discard
    } else {
      return true
    }
  }

  const onGridUpdatedHandler = React.useCallback(
    (rows: DataGridUpdatedRow[]) => {
      if (activeBalance !== null && activeBalance.data !== undefined) {
        setPendingChanges(true)

        let firstModifiedDate = balancePeriod.start

        //60157 - Excluimos el balance de portugal del PVB ya que no tiene existencias y queremos que se actualizen los linepack acumulados a pasado
        if (
          activeBalance !== null &&
          activeBalance.data !== undefined &&
          !(
            enumKeyToValue(EnumZonaBalance, activeBalance.zone) == EnumZonaBalance.PT &&
            enumKeyToValue(EnumTipoBalance, activeBalance.tipo) == EnumTipoBalance.PVB
          )
        ) {
          firstModifiedDate = moment(activeBalance.data[rows[0].row]['date'], 'DD/MM/YYYY').toDate()
        }

        calculateBalanceRows({ ...activeBalance }, rows).then(modifiedOperations => {
          let updatedBalance = activeBalance
          updatedBalance.operations = modifiedOperations

          setActiveBalance(recalculateBalance(updatedBalance, firstModifiedDate, balancePeriod.end))
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBalance, pendingChanges, calculateBalanceRows, recalculateBalance]
  )

  const onDiscardChanges = () => {
    setPendingChanges(false)
    activeBalance && getBalanceData({ ...activeBalance })
  }

  const onSaveChanges = async () => {
    if (pendingChanges) {
      //Si el cálculo de balance no ha terminado o los datos no están actualizados, pedimos confirmación para guardar
      if (
        balanceStatus.inProcess ||
        (balanceStatus.lastExecutionTime &&
          (balanceStatus.displayTime ?? moment().toDate()) < balanceStatus.lastExecutionTime)
      ) {
        await getConfirmation(ConfirmationDialogs.SAVE_DATA_TIMED_OUT).then(async () => {
          await saveBalance(activeBalance!).then(() => {
            setPendingChanges(false)
            closeConfirmation()
            activeBalance && setActiveBalance(resetOperationsModifiedFlag(activeBalance))

            info(t('balance:notifications.success.saved'))
            activeBalance && getBalanceData(activeBalance)
          })
        })
      } else {
        await saveBalance(activeBalance!).then(() => {
          setPendingChanges(false)
          activeBalance && setActiveBalance(resetOperationsModifiedFlag(activeBalance))

          info(t('balance:notifications.success.saved'))
          activeBalance && getBalanceData(activeBalance)
        })
      }
    } else {
      info(t('balance:notifications.info.noPendingChanges'))
    }
  }

  //#endregion

  //#region Effects

  const { showError, clearError, errorProps } = useHttpError()

  useEffect(() => {
    matchesSmallDevices && setBalancePeriod({ start: moment().toDate(), end: moment().toDate() })
    !matchesSmallDevices &&
      setBalancePeriod({
        start: matchesSmallDevices ? moment().toDate() : moment().add(-5, 'days').toDate(),
        end: matchesSmallDevices ? moment().toDate() : moment().add(15, 'days').toDate()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesSmallDevices])

  useEffect(() => {
    getBalances()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, activeBalanceZone])

  useEffect(() => {
    activeBalance && getBalanceData({ ...activeBalance })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balancePeriod])

  useEffect(() => {
    if (balances && balances.length > 0) {
      getBalanceData(balances[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances])

  useEffect(() => {
    const getTodayDateIndex = () => {
      if (activeBalance && activeBalance !== undefined) {
        const index = activeBalance.data?.findIndex(
          x =>
            moment(x['date'], 'DD/MM/YYYY').format('DD/MM/YYYY') === moment().format('DD/MM/YYYY')
        )

        if (index !== undefined && index !== -1) {
          setTodayIndexes([index])
        } else {
          setTodayIndexes([])
        }
      }
    }

    const getPastDateIndexes = () => {
      if (activeBalance && activeBalance !== undefined && activeBalance.operations !== undefined) {
        const pastDateOperationsIndexes: number[] = []

        activeBalance.data?.forEach((operation, index) => {
          if (moment(operation['date'], 'DD/MM/YYYY').isBefore(moment(), 'day')) {
            pastDateOperationsIndexes.push(index)
          }
        })

        if (pastDateOperationsIndexes !== undefined && pastDateOperationsIndexes.length > 0) {
          setPastDatesIndexes(pastDateOperationsIndexes)
        } else {
          setPastDatesIndexes([])
        }
      }
    }
    getPastDateIndexes()
    getTodayDateIndex()

    activeBalance && setActiveBalancecolumns(activeBalance?.columns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBalance?.data])

  //Eventos
  useEffect(() => {
    //getEventsReceived, addEventListener, deleteEventListener
    const eventReceived = (event: string, date: Date, data: any) => {
      switch (event) {
        case 'UpdatedBalance':
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: date
          }))
          break
        case 'UpdatingBalance':
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: date
          }))
          break
      }
    }

    addEventListener('Balance', [], { onEventReceivedHandler: eventReceived } as OnEventReceived)

    const lastEventReceived = getEventsReceived([
      'UpdatedBalance',
      'UpdatingBalance',
      'UpdateFailedBalance'
    ])
    if (lastEventReceived && lastEventReceived?.length > 0) {
      switch (lastEventReceived[0].event) {
        case 'UpdatedBalance':
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: lastEventReceived[0].date
          }))
          break
        case 'UpdatingBalance':
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: lastEventReceived[0].date
          }))
          break
      }
    } else {
      getStatusBalance().then(status => {
        setBalanceStatus(prevState => ({
          ...prevState,
          inProcess: status?.inProcess ?? false,
          lastExecutionTime: status?.lastExecutionTime
        }))
      })
    }

    return () => {
      deleteEventListener()
    }
  }, [])

  useEffect(() => {
    if (balanceStatus && balanceStatus.lastExecutionTime) {
      if (balanceStatus.inProcess) {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('balance:globalNotifications.calculation.messages.onCalculation')
        })
      } else if (
        (balanceStatus.displayTime ?? moment().toDate()) < balanceStatus.lastExecutionTime
      ) {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('balance:globalNotifications.calculation.messages.onFinish'),
          actions: [
            {
              label: t('balance:globalNotifications.calculation.actions.refresh'),
              onClick: async () => {
                getBalances()
              }
            }
          ]
        })
      } else {
        setGlobalNotification(undefined)
      }
    }
  }, [balanceStatus])

  //#endregion

  //#region Auxiliar functions

  const getBalances = async () => {
    clearError()
    setBalances(
      await fetchBalances().then(response => {
        if (response.success) {
          clearError()
          return response.data
        } else {
          showError({
            title: t('balance:title'),
            errorCode: response.error!.code,
            onRefresh: getBalances
          })
          return null
        }
      })
    )
  }

  const getBalanceData = async (balance: BalanceModel) => {
    clearError()
    setIsFetching(true)
    setActiveBalance(null)

    setActiveBalance(
      await fetchBalanceData(balance.id, balancePeriod.start, balancePeriod.end).then(response => {
        setIsFetching(false)

        if (response.success) {
          clearError()
          if (!balanceStatus.inProcess) setGlobalNotification(undefined)
          if (!response.data) return null
          setBalanceStatus(prevState => ({ ...prevState, displayTime: new Date() }))
          const calculatedBalance = calculateBalance(
            balance,
            response.data,
            balancePeriod.start,
            balancePeriod.end
          )

          if (calculatedBalance && calculatedBalance.data && calculatedBalance.operations) {
            calculatedBalance.data = enhanceBalanceDataWithColumnHighlighting(
              calculatedBalance.data,
              calculatedBalance.operations,
              calculatedBalance.columnsConfiguration
            )
          }

          return calculatedBalance
        } else {
          showError({
            title: t('balance:title'),
            errorCode: response.error!.code,
            onRefresh: getBalances
          })
          return null
        }
      })
    )
  }

  const resetBalanceData = async (balance: BalanceModel) => {
    setActiveBalance(null)
    setActiveBalance({ ...balance, data: [] })
  }

  //#endregion

  return (
    <>
      {!matchesSmallDevices ? (
        // <Page error={errorProps} notification={globalNotification}>
        <Page notification={globalNotification}>
          <PageHeader title={t('balance:title')}>
            <DateFilter
              enabled={!isFetching}
              initialFilter={DateFilters.CUSTOM}
              start={balancePeriod.start}
              end={balancePeriod.end}
              onSelectDate={onSelectDateHandler}
              onValidatePrerequisites={onValidatePrerequisites}
              filters={[{ filter: DateFilters.CUSTOM }]}
              configuration={{ maxPeriod: DateFilterPeriods.SEMESTER }}
            ></DateFilter>
          </PageHeader>
          <Tabs
            value={activeTab}
            onChange={onChangeTabHandler}
            indicatorColor="primary"
            textColor="primary"
          >
            {balances &&
              balances.map((balance, index) => {
                return (
                  <Tab
                    disabled={isFetching}
                    key={index}
                    label={balance.description}
                    {...a11yProps(index)}
                    onClick={() => {
                      onClickTabHandler(balance, index)
                    }}
                  />
                )
              })}
          </Tabs>
          <Card className={classes.balanceCard}>
            <CardActions>
              <XLSXExport
                columns={activeBalanceColumns || []}
                data={activeBalance && activeBalance.data ? activeBalance.data : []}
                actionButton={
                  <ButtonWithPermissions
                    disabled={isFetching || !activeBalance || !activeBalance.data}
                    size="small"
                    color="primary"
                    onClick={() => {}}
                    hasPermission={hasPermission(Resources.Balance, [
                      UserPermission.READ_ONLY,
                      UserPermission.EDIT
                    ])}
                  >
                    {t('balance:dataGrid.customActions.download')}
                  </ButtonWithPermissions>
                }
              />
              <ButtonWithPermissions
                disabled={!pendingChanges}
                size="small"
                color="primary"
                onClick={onDiscardChanges}
                hasPermission={hasPermission(Resources.Balance, [UserPermission.EDIT])}
              >
                {t('balance:dataGrid.customActions.discard')}
              </ButtonWithPermissions>
              <ButtonWithPermissions
                disabled={!pendingChanges}
                variant="contained"
                size="small"
                color="primary"
                onClick={onSaveChanges}
                hasPermission={hasPermission(Resources.Balance, [UserPermission.EDIT])}
              >
                {t('balance:dataGrid.customActions.save')}
              </ButtonWithPermissions>
            </CardActions>
            <CardContent>
              <GlobalerisDataGrid
                columns={activeBalanceColumns || []}
                data={activeBalance && activeBalance.data ? activeBalance.data : []}
                options={options}
                readOnlyRowsIndexes={pastDatesIndexes}
                highlightedRowsIndexes={todayIndexes}
                onGridUpdated={onGridUpdatedHandler}
                userPermissions={getUserPermissions(Pages.Balance)}
                info={DataTableErrorHandler(errorProps)}
              />
            </CardContent>
          </Card>
        </Page>
      ) : (
        <BalanceResponsivePage></BalanceResponsivePage>
      )}
    </>
  )
}
